import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { Sparkle } from "components/icons/Sparkle";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import ToolTip from "components/design/toolTip";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import dayjs from "dayjs";
import { DISPLAY_DATE } from "constants/date";
import { Transactions } from "types/Models/books";
import { ArrowDown } from "components/icons/ArrowDown";
import { CollapseNavIcon } from "components/icons/CollapseNavIcon";
import { useMemo, useState } from "react";
import {
  AI_RECONCILE_MISSING_TXN_ACTION,
  AI_RECONCILE_SECTION_TYPE,
  AIReconcileMissingActionType,
  AIReconcileTxnActionType,
} from "constants/dataSource";
import { ExpandNavIcon } from "components/icons/ExpandNavIcon";
import { TableUI } from "components/design/TableUI";
import { ColumnDef, getCoreRowModel, useReactTable } from "react-table-8.10.7";
import { HeadCellContent } from "components/DesignSystem/HeadCellContent/HeadCellContent";
import Table from "components/DesignSystem/Table/V2/Table";
import { flexRender } from "react-table-8.10.7";
import Wind from "components/icons/Wind";

type StatementTxn = {
  date: string;
  description: string;
  amount: number;
};

type PairedTransaction = {
  statement_txn: StatementTxn | null;
  books_txn: Transactions;
  date_diff_days?: number;
};

type BaseProps = {
  title: string;
  subtitle: string;
};

type PairedSectionProps = BaseProps & {
  sectionType: typeof AI_RECONCILE_SECTION_TYPE.PAIRED;
  pairedTransactions: PairedTransaction[];
  txnActions: Record<string, AIReconcileTxnActionType>;
  onTxnActionChange: (txnId: string, action: AIReconcileTxnActionType) => void;
  getDefaultAction: (txn: Transactions) => AIReconcileTxnActionType;
  emptyMessage?: string;
};

type MissingSectionProps = BaseProps & {
  sectionType: typeof AI_RECONCILE_SECTION_TYPE.MISSING_IN_BOOKS;
  missingTransactions: StatementTxn[];
  missingTxnActions: Record<number, AIReconcileMissingActionType>;
  onMissingTxnActionChange: (
    index: number,
    action: AIReconcileMissingActionType
  ) => void;
};

type Props = PairedSectionProps | MissingSectionProps;

const ACTION_LABELS: Record<AIReconcileTxnActionType, string> = {
  reconcile: "Reconcile",
  do_not_reconcile: "Do not reconcile",
  exclude: "Exclude transaction",
};

const MISSING_ACTION_LABELS: Record<AIReconcileMissingActionType, string> = {
  create_and_reconcile: "Create and reconcile",
  do_not_create: "Do not create",
};

const TruncatedDescription = ({ text }: { text: string }) => (
  <ToolTip text={text}>
    <span className="t-truncate t-block t-max-w-[300px]">{text}</span>
  </ToolTip>
);

const STATEMENT_BASE_COLUMNS: ColumnDef<StatementTxn>[] = [
  {
    accessorKey: "date",
    id: "date",
    size: 30,
    header: () => <HeadCellContent title="Date" align="left" />,
    cell: ({ row }) => dayjs(row.original.date).format(DISPLAY_DATE),
  },
  {
    accessorKey: "description",
    id: "description",
    size: 40,
    header: () => <HeadCellContent title="Description" align="left" />,
    cell: ({ row }) => (
      <TruncatedDescription text={row.original.description || "-"} />
    ),
  },
  {
    accessorKey: "amount",
    id: "amount",
    size: 30,
    header: () => <HeadCellContent title="Amount" align="right" />,
    cell: ({ row }) => (
      <div className="t-text-right">
        <AmountSuperScript amount={row.original.amount} />
      </div>
    ),
  },
];

const TxnActionDropdown = ({
  txnId,
  currentAction,
  onActionChange,
}: {
  txnId: string;
  currentAction: AIReconcileTxnActionType;
  onActionChange: (txnId: string, action: AIReconcileTxnActionType) => void;
}) => {
  return (
    <Dropdown.Root>
      <Dropdown.Trigger asChild>
        <button className="all:unset t-group t-flex t-justify-between t-items-center t-gap-1 t-px-3 t-py-1.5 t-border t-border-solid t-border-neutral-10 t-rounded-lg t-text-body-sm t-cursor-pointer t-whitespace-nowrap t-select-none t-w-40">
          {ACTION_LABELS[currentAction]}
          <div className="group-data-state-open:-t-rotate-180 t-transform t-transition t-duration-300 t-ease-in-out">
            <ArrowDown color="currentColor" />
          </div>
        </button>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content sideOffset={4} side="bottom" align="end">
          {(Object.keys(ACTION_LABELS) as AIReconcileTxnActionType[]).map(
            (action) => (
              <Dropdown.Item
                key={action}
                onSelect={() => onActionChange(txnId, action)}
              >
                {ACTION_LABELS[action]}
              </Dropdown.Item>
            )
          )}
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
};

const MissingTxnActionDropdown = ({
  index,
  currentAction,
  onActionChange,
}: {
  index: number;
  currentAction: AIReconcileMissingActionType;
  onActionChange: (index: number, action: AIReconcileMissingActionType) => void;
}) => {
  return (
    <Dropdown.Root>
      <Dropdown.Trigger asChild>
        <button className="all:unset t-group t-flex t-justify-between t-items-center t-gap-1 t-px-3 t-py-1.5 t-border t-border-solid t-border-neutral-10 t-rounded-lg t-text-body-sm t-cursor-pointer t-whitespace-nowrap t-select-none t-w-[170px]">
          {MISSING_ACTION_LABELS[currentAction]}
          <div className="group-data-state-open:-t-rotate-180 t-transform t-transition t-duration-300 t-ease-in-out">
            <ArrowDown color="currentColor" />
          </div>
        </button>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content sideOffset={4} side="bottom" align="end">
          {(
            Object.keys(MISSING_ACTION_LABELS) as AIReconcileMissingActionType[]
          ).map((action) => (
            <Dropdown.Item
              key={action}
              onSelect={() => onActionChange(index, action)}
            >
              {MISSING_ACTION_LABELS[action]}
            </Dropdown.Item>
          ))}
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
};

export const AIReconcileAccordionSection = (props: Props) => {
  const { title, subtitle, sectionType } = props;

  const [openItems, setOpenItems] = useState<string[]>([]);

  let count = 0;
  if (sectionType === AI_RECONCILE_SECTION_TYPE.PAIRED) {
    count = props.pairedTransactions.length;
  } else {
    count = props.missingTransactions.length;
  }

  if (count === 0) return null;

  return (
    <Accordion.Root
      type="multiple"
      value={openItems}
      onValueChange={setOpenItems}
    >
      <Accordion.Item value={title}>
        <Accordion.Trigger className="!t-normal-case">
          <div className="t-flex t-gap-2">
            <span className="t-p-0.1">
              <Sparkle />
            </span>

            <div className="t-flex t-flex-col t-items-start t-gap-1">
              <span className="t-text-body-sm t-text-text-60">
                {count} {title}
              </span>
              <span className="t-text-body-sm-italic t-italic t-text-text-30">
                {subtitle}
              </span>
            </div>
          </div>
        </Accordion.Trigger>
        <Accordion.Content>
          {sectionType === AI_RECONCILE_SECTION_TYPE.PAIRED && (
            <PairedTransactionsContent
              pairedTransactions={props.pairedTransactions}
              actions={props.txnActions}
              onActionChange={props.onTxnActionChange}
              getDefaultAction={props.getDefaultAction}
              emptyMessage={props.emptyMessage}
            />
          )}
          {sectionType === AI_RECONCILE_SECTION_TYPE.MISSING_IN_BOOKS && (
            <StatementOnlyTransactionsTable
              transactions={props.missingTransactions}
              actions={props.missingTxnActions}
              onActionChange={props.onMissingTxnActionChange}
            />
          )}
        </Accordion.Content>
      </Accordion.Item>
    </Accordion.Root>
  );
};

const TableHeader = ({ title }: { title: string }) => (
  <div className="t-flex t-items-center t-px-4 t-py-4">
    <span className="t-text-subtext t-text-text-60">{title}</span>
  </div>
);

const StatementTable = ({
  statementTxns,
  emptyContent,
}: {
  statementTxns: StatementTxn[];
  emptyContent?: string;
}) => {
  const table = useReactTable({
    data: statementTxns,
    columns: STATEMENT_BASE_COLUMNS,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      <div className="t-flex t-items-center t-justify-between t-border-0 t-border-b t-border-solid t-border-neutral-10 t-bg-neutral-0">
        <TableHeader title="Account statement uploaded" />
      </div>
      {statementTxns.length === 0 ? (
        <div className="t-flex-1 t-w-full t-flex t-flex-col t-items-center t-justify-center t-text-text-30 t-text-body-sm t-gap-2">
          <Wind />
          <div>{emptyContent}</div>
        </div>
      ) : (
        <TableUI table={table} sizeType="percent">
          {({ row }) => (
            <Table.Row key={row.id} className="t-h-[55px]">
              {row.getVisibleCells().map((cell) => (
                <Table.Cell
                  key={cell.id}
                  style={{ width: `${cell.column.getSize()}%` }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Table.Cell>
              ))}
            </Table.Row>
          )}
        </TableUI>
      )}
    </>
  );
};

const PairedTransactionsContent = ({
  pairedTransactions,
  actions,
  onActionChange,
  getDefaultAction,
  emptyMessage,
}: {
  pairedTransactions: PairedTransaction[];
  actions: Record<string, AIReconcileTxnActionType>;
  onActionChange: (txnId: string, action: AIReconcileTxnActionType) => void;
  getDefaultAction: (txn: Transactions) => AIReconcileTxnActionType;
  emptyMessage?: string;
}) => {
  const [open, setOpen] = useState(true);

  const booksTxns = pairedTransactions
    .map((p) => p.books_txn)
    .filter((txn): txn is Transactions => Boolean(txn));

  const statementTxns = pairedTransactions
    .map((p) => p.statement_txn)
    .filter((txn): txn is StatementTxn => Boolean(txn));

  return (
    <div className="t-relative t-border t-border-solid t-border-neutral-10 t-rounded-2xl t-overflow-hidden t-bg-white">
      <div className="t-flex">
        <div className={open ? "t-w-[57%]" : "t-w-full"}>
          <BooksTransactionsTable
            transactions={booksTxns}
            actions={actions}
            onActionChange={onActionChange}
            getDefaultAction={getDefaultAction}
          />
        </div>

        {open && (
          <div className="t-w-[43%] t-bg-neutral-5 t-border-l t-border-0 t-border-solid t-border-neutral-10 t-flex t-flex-col">
            <StatementTable
              statementTxns={statementTxns}
              emptyContent={emptyMessage}
            />
          </div>
        )}
      </div>

      <button
        onClick={() => setOpen(!open)}
        className="all:unset t-absolute t-top-3 t-right-3 t-cursor-pointer t-z-10"
      >
        {open ? (
          <ExpandNavIcon className="t-text-text-30" />
        ) : (
          <CollapseNavIcon className="t-text-text-30" />
        )}
      </button>
    </div>
  );
};

const BooksTransactionsTable = ({
  transactions,
  actions,
  onActionChange,
  getDefaultAction,
}: {
  transactions: Transactions[];
  actions: Record<string, AIReconcileTxnActionType>;
  onActionChange: (txnId: string, action: AIReconcileTxnActionType) => void;
  getDefaultAction: (txn: Transactions) => AIReconcileTxnActionType;
}) => {
  const columns: ColumnDef<Transactions>[] = useMemo(
    () => [
      {
        accessorFn: (row) => row.transaction.date,
        id: "date",
        size: 20,
        header: () => <HeadCellContent title="Date" align="left" />,
        cell: ({ row }) =>
          dayjs(row.original.transaction.date).format(DISPLAY_DATE),
      },
      {
        accessorFn: (row) => row.transaction.description,
        id: "description",
        size: 40,
        header: () => <HeadCellContent title="Description" align="left" />,
        cell: ({ row }) => (
          <TruncatedDescription
            text={row.original.transaction.description || "-"}
          />
        ),
      },
      {
        accessorFn: (row) => row.transaction.amount,
        id: "amount",
        size: 20,
        header: () => <HeadCellContent title="Amount" align="right" />,
        cell: ({ row }) => (
          <div className="t-text-right">
            <AmountSuperScript amount={row.original.transaction.amount} />
          </div>
        ),
      },
      {
        id: "actions",
        size: 20,
        header: () => <HeadCellContent title="Actions" align="left" />,
        cell: ({ row }) => {
          const txnId = row.original.transaction.uuid;
          const currentAction =
            actions[txnId] || getDefaultAction(row.original);

          return (
            <TxnActionDropdown
              txnId={txnId}
              currentAction={currentAction}
              onActionChange={onActionChange}
            />
          );
        },
      },
    ],
    [actions, onActionChange, getDefaultAction]
  );

  const table = useReactTable({
    data: transactions,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      <div className="t-flex t-items-center t-justify-between t-border-0 t-border-b t-border-solid t-border-neutral-10 t-bg-purple-0">
        <TableHeader title="Inkle Books Transactions" />
      </div>

      <TableUI table={table} sizeType="percent">
        {({ row }) => (
          <Table.Row key={row.id} className="t-h-[55px]">
            {row.getVisibleCells().map((cell) => (
              <Table.Cell
                key={cell.id}
                style={{ width: `${cell.column.getSize()}%` }}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </Table.Cell>
            ))}
          </Table.Row>
        )}
      </TableUI>
    </>
  );
};

const MissingBooksTable = ({
  transactions,
  actions,
  onActionChange,
}: {
  transactions: StatementTxn[];
  actions: Record<number, AIReconcileMissingActionType>;
  onActionChange: (index: number, action: AIReconcileMissingActionType) => void;
}) => {
  const columns: ColumnDef<StatementTxn>[] = useMemo(
    () => [
      ...STATEMENT_BASE_COLUMNS,
      {
        id: "actions",
        size: 20,
        header: () => <HeadCellContent title="Actions" align="left" />,
        cell: ({ row }: { row: { index: number; original: StatementTxn } }) => {
          const currentAction =
            actions[row.index] ||
            AI_RECONCILE_MISSING_TXN_ACTION.CREATE_AND_RECONCILE;

          return (
            <MissingTxnActionDropdown
              index={row.index}
              currentAction={currentAction}
              onActionChange={onActionChange}
            />
          );
        },
      },
    ],
    [actions, onActionChange]
  );

  const table = useReactTable({
    data: transactions,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      <div className="t-flex t-items-center t-justify-between t-border-0 t-border-b t-border-solid t-border-neutral-10 t-bg-purple-0">
        <TableHeader title="Inkle Books Transactions" />
      </div>

      <TableUI table={table} sizeType="percent">
        {({ row }) => (
          <Table.Row key={row.id} className="t-h-[55px]">
            {row.getVisibleCells().map((cell) => (
              <Table.Cell
                key={cell.id}
                style={{ width: `${cell.column.getSize()}%` }}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </Table.Cell>
            ))}
          </Table.Row>
        )}
      </TableUI>
    </>
  );
};

const StatementOnlyTransactionsTable = ({
  transactions,
  actions,
  onActionChange,
}: {
  transactions: StatementTxn[];
  actions: Record<number, AIReconcileMissingActionType>;
  onActionChange: (index: number, action: AIReconcileMissingActionType) => void;
}) => {
  const [open, setOpen] = useState(true);

  return (
    <div className="t-relative t-border t-border-solid t-border-neutral-10 t-rounded-2xl t-overflow-hidden t-bg-white">
      <div className="t-flex">
        <div className={open ? "t-w-[57%]" : "t-w-full"}>
          <MissingBooksTable
            transactions={transactions}
            actions={actions}
            onActionChange={onActionChange}
          />
        </div>

        {open && (
          <div className="t-w-[43%] t-bg-neutral-5 t-border-l t-border-0 t-border-solid t-border-neutral-10 t-flex t-flex-col">
            <StatementTable statementTxns={transactions} />
          </div>
        )}
      </div>

      <button
        onClick={() => setOpen(!open)}
        className="all:unset t-absolute t-top-3 t-right-3 t-cursor-pointer t-z-10"
      >
        {open ? (
          <ExpandNavIcon className="t-text-text-30" />
        ) : (
          <CollapseNavIcon className="t-text-text-30" />
        )}
      </button>
    </div>
  );
};
